import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { api } from "../../utils/api";
import axios from "axios";
import {
  FileText,
  Download,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Upload,
  Eye,
  Edit2,
  Save,
  X,
  MessageSquare,
  User,
  CheckCircle2,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import AdditionalInfoBuilderModal from "../../components/AdditionalInfoBuilderModal";
import DocumentPreviewModal from "../../components/DocumentPreviewModal";
import PdfPreview from "../../components/PdfPreview";
import { useToast } from "../../contexts/ToastContext";
import { MASTER_DATA } from "@/pages/user/forms/config/masterData";

const SECTIONS = [
  {
    key: "basic_info",
    title: "Basic Information",
    fields: [
      ["passport_type", "Passport Type"],
      ["nationality", "Nationality"],
      ["port_of_arrival", "Port of Arrival"],
      ["date_of_birth", "Date of Birth"],
      ["email_id", "Email ID"],
      ["expected_date_of_arrival", "Expected Date of Arrival"],
    ],
  },
  {
    key: "personal_details",
    title: "Personal Details",
    fields: [
      ["surname", "Surname"],
      ["given_name", "Given Name"],
      ["gender", "Gender"],
      ["town_city_of_birth", "Town/City of Birth"],
      ["country_of_birth", "Country of Birth"],
      ["citizenship_national_id", "Citizenship/National ID"],
      ["religion", "Religion"],
      ["identity_marks", "Visible Identification Marks"],
      ["educational_qualification", "Educational Qualification"],
      ["acquired_qualification", "Acquired Qualification"],
      ["nationality_acquisition", "Nationality Acquisition"],
      ["prev_nationality", "Previous Nationality"],
      ["name_changed", "Name Changed"],
      ["previous_surname", "Previous Surname"],
      ["previous_given_name", "Previous Given Name"],
      ["lived_2_years", "Lived 2+ Years (Past 5 Years)"],
      ["father_name", "Father Name"],
      ["father_nationality", "Father Nationality"],
      ["father_prev_nationality", "Father Previous Nationality"],
      ["father_pob", "Father Place of Birth"],
      ["father_cob", "Father Country of Birth"],
      ["mother_name", "Mother Name"],
      ["mother_nationality", "Mother Nationality"],
      ["mother_prev_nationality", "Mother Previous Nationality"],
      ["mother_pob", "Mother Place of Birth"],
      ["mother_cob", "Mother Country of Birth"],
      ["marital_status", "Marital Status"],
      ["spouse_name", "Spouse Name"],
      ["spouse_nationality", "Spouse Nationality"],
      ["spouse_birth_place", "Spouse Place of Birth"],
      ["spouse_birth_country", "Spouse Country of Birth"],
    ],
  },
  {
    key: "occupation_info",
    title: "Occupation Details of Applicant",
    fields: [
      ["occupation", "Occupation"],
      ["employer_name", "Employer Name"],
      ["designation", "Designation"],
      ["employer_address", "Employer Address"],
      // ["employer_phone", "Employer Phone"],
    ],
  },
  {
    key: "company_info",
    title: "Details of Applicant's Company",
    fields: [
      ["applicant_company_name", "Company Name"],
      ["applicant_company_website", "Company Website"],
      ["applicant_company_address_phone", "Company Address & Phone"],
      ["nature_of_business_product", "Nature of Business / Product"],
    ],
  },
  {
    key: "passport_info",
    title: "Passport Information",
    fields: [
      ["passport_number", "Passport Number"],
      ["passport_place_of_issue", "Place of Issue"],
      ["passport_date_of_issue", "Date of Issue"],
      ["passport_date_of_expiry", "Date of Expiry"],
      ["other_passport", "Other Passport?"],
      ["other_passport_country", "Other Passport Country"],
      ["other_passport_no", "Other Passport No."],
      ["other_passport_doi", "Other Passport DOI"],
      ["other_passport_poi", "Other Passport POI"],
      ["other_passport_nationality", "Other Passport Nationality"],
    ],
  },
  {
    key: "present_address",
    title: "Present Address",
    fields: [
      ["present_address_street", "Street"],
      ["present_address_city", "City"],
      ["present_address_state", "State"],
      ["present_address_zip", "ZIP"],
      ["present_address_country", "Country"],
      ["present_phone", "Phone"],
    ],
  },
  {
    key: "permanent_address",
    title: "Permanent Address",
    fields: [
      ["same_address_checkbox", "Same as Present (Y/N)"],
      ["permanent_street", "Street"],
      ["permanent_city", "City"],
      ["permanent_state", "State"],
    ],
  },
  {
    key: "heritage",
    title: "Heritage & Background",
    fields: [
      ["pak_heritage", "Any Pakistani Heritage"],
      ["pak_heritage_details", "Heritage Details"],
    ],
  },
  {
    key: "military_organization",
    title: "Military Organization",
    fields: [
      ["military_organization", "Military Organization Name"],
      ["military_designation", "Designiation"],
      ["military_posting", "Military Posting"],
      ["military_rank", "Military Rank"],
    ],
  },
  {
    key: "hotel_details",
    title: "Hotel Details",
    fields: [
      ["hotel_resort_name", "Hotel/Resort Name"],
      ["hotel_resort_city", "City"],
    ],
  },
  {
    key: "visa_details",
    title: "Visa Details",
    fields: [
      ["places_to_visit", "Places to Visit"],
      ["exit_port", "Exit Port"],
    ],
  },
  {
    key: "previous_india",
    title: "Previous India Visit",
    fields: [
      ["visited_india_before", "Visited India Before"],
      ["prev_stay_address", "Previous Stay Address"],
      ["cities_visited", "Cities Visited"],
      ["last_visa_no", "Last Visa No."],
      ["last_visa_type", "Last Visa Type"],
      ["last_visa_poi", "Last Visa POI"],
      ["last_visa_doi", "Last Visa DOI"],
      ["permission_refused", "Permission Refused"],
      ["refusal_details", "Refusal Details"],
      ["countries_visited_10_years", "Countries Visited in Last 10 Years"],
    ],
  },
  {
    key: "saarc_info",
    title: "SAARC Visit Information",
    fields: [
      ["saarc_visit", "SAARC Visit?"],
      ["saarc_visits", "SAARC Visits"],
    ],
  },
  {
    key: "references",
    title: "References",
    fields: [
      ["reference_india_name", "India Ref Name"],
      ["reference_india_address", "India Ref Address"],
      ["reference_india_phone", "India Ref Phone"],
      ["reference_home_name", "Home Ref Name"],
      ["reference_home_address", "Home Ref Address"],
      ["reference_home_phone", "Home Ref Phone"],
    ],
  },
  {
    key: "declaration",
    title: "Declaration",
    fields: [
      ["arrested", "Arrested?"],
      ["deported", "Deported?"],
      ["human_trafficking", "Human Trafficking?"],
      ["cyber_crime", "Cyber Crime?"],
      ["extremist_views", "Extremist Views?"],
      ["asylum", "Asylum?"],
    ],
  },
];

// Utility Functions

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const isPDF = (filename) => filename?.toLowerCase().endsWith(".pdf");

const badge = (status) => {
  const statusMap = {
    "Application Received, Pending for Review": "bg-yellow-100 text-yellow-800",
    "Additional Information Requested": "bg-orange-100 text-orange-700",
    "Additional Information Received": "bg-blue-100 text-blue-800",
    "Review Passed, Pending for Submission": "bg-purple-100 text-purple-800",
    "Submission Done, Awaiting Visa Status": "bg-indigo-100 text-indigo-800",
    "Visa Approved": "bg-green-100 text-green-800",
    "Visa Rejected": "bg-red-100 text-red-800",
  };
  return statusMap[status] || "bg-gray-100 text-gray-800";
};

export default function ViewAdminApplication() {
  const { showToast } = useToast();
  const { id } = useParams();
  const navigate = useNavigate();

  const [application, setApplication] = useState(null);
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editPassengerIndex, setEditPassengerIndex] = useState(null);
  const [editablePassengers, setEditablePassengers] = useState([]);

  const [statusForm, setStatusForm] = useState({
    status: "",
    note: "",
    rejection_reason: "",
  });

  const [rounds, setRounds] = useState([]);
  const [loadingRounds, setLoadingRounds] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);

  const [openSection, setOpenSection] = useState({});
  const [docModal, setDocModal] = useState({ open: false, index: 0 });
  // ✅ ADD: State for dropdown data from API
  const [dropdownData, setDropdownData] = useState({
    nationality: [],
    port_of_entry: [],
    port_of_exit: [],
    previous_nationalities: [],
    other_nationalities: [],
    country_of_birth: [],
  });

  // ✅ ADD THIS: Map field names to their dropdown options
  const DROPDOWN_FIELDS = {
    gender: MASTER_DATA.gender,
    religion: MASTER_DATA.religion,
    educational_qualification: MASTER_DATA.educational_qualification,
    nationality_acquisition: MASTER_DATA.nationality_acquisition,
    passport_type: MASTER_DATA.passport_type,
    marital_status: MASTER_DATA.marital_status,
    saarc_country: MASTER_DATA.saarc_country,

    // Dynamic dropdowns from API
    nationality: dropdownData.nationality,
    father_nationality: dropdownData.nationality,
    mother_nationality: dropdownData.nationality,
    spouse_nationality: dropdownData.nationality,
    previous_nationality: dropdownData.previous_nationalities,
    other_passport_nationality: dropdownData.other_nationalities,
    country_of_birth: dropdownData.country_of_birth,
    present_address_country: dropdownData.country_of_birth,
    permanent_address_country: dropdownData.country_of_birth,
    port_of_arrival: dropdownData.port_of_entry,
    port_of_exit: dropdownData.port_of_exit,
    father_cob:dropdownData.country_of_birth,
    mother_cob:dropdownData.country_of_birth,
    spouse_cob:dropdownData.country_of_birth,
  };

  /* -------------------------
         Load Dropdowns from API
    -------------------------- */
  useEffect(() => {
    fetchDropdowns();
  }, []);

  const fetchDropdowns = async () => {
    try {
      const response = await api.getAlldropdowns();
      setDropdownData({
        nationality: response.data.nationalities || [],
        port_of_entry: response.data.port_of_entry || [],
        port_of_exit: response.data.port_of_exit || [],
        previous_nationalities: response.data.previous_nationalities || [],
        other_nationalities: response.data.other_nationalities || [],
        country_of_birth: response.data.country_of_birth || [],
      });
    } catch (error) {
      console.error("Error fetching dropdowns:", error);
      showToast("Failed to load dropdown options", "error");
    }
  };

  /* -------------------------
             Load Application
        -------------------------- */
  useEffect(() => {
    fetchApplication();
  }, [id]);

  const fetchApplication = async () => {
    try {
      const res = await api.adminGetApplication(id);
      setApplication(res.data);
      setPassengers(res.data.passengers || []);
      setEditablePassengers(
        JSON.parse(JSON.stringify(res.data.passengers || [])),
      );
      setStatusForm((prev) => ({ ...prev, status: res.data.status }));
      if (res.data.passengers?.length > 0)
        setOpenSection({ [`p0.${SECTIONS[0].key}`]: true });
    } catch {
      showToast("Failed to load application", "error");
      navigate("/admin/applications");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------
            Load Additional Info
        -------------------------- */
  useEffect(() => {
    if (application?.id) fetchAdditionalInfoRounds();
  }, [application?.id]);

  const fetchAdditionalInfoRounds = async () => {
    try {
      setLoadingRounds(true);
      const res = await api.adminListAdditionalInfoRounds(id);
      setRounds(res.data.rounds || []);
    } catch {
      showToast("Failed to load Additional Info", "error");
    } finally {
      setLoadingRounds(false);
    }
  };

  /* -------------------------
             Toggle Section (Accordion)
        -------------------------- */
  const toggleSection = (key) =>
    setOpenSection((s) => ({ ...s, [key]: !s[key] }));

  /* -------------------------
             Update Status
        -------------------------- */
  const updateStatus = async () => {
    try {
      await api.adminUpdateApplicationStatus(id, statusForm);
      showToast("Status Updated", "success");
      fetchApplication();
    } catch (e) {
      showToast(e.response?.data?.detail || "Update failed", "error");
    }
  };

  /* -------------------------
             ZIP Export
        -------------------------- */
  const exportZip = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/applications/${id}/export`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: "blob",
        },
      );
      saveBlob(res.data, `${id}.zip`);
    } catch {
      showToast("ZIP export failed", "error");
    }
  };

  // Upload visa
  const handleVisaUpload = async (file) => {
    const formData = new FormData();
    formData.append("final_visa_file", file);

    try {
      await api.adminUploadVisa(id, formData);
      fetchApplication();
      alert("Visa document uploaded successfully!");
    } catch (error) {
      console.error("Visa upload failed:", error);
      alert("Failed to upload visa document");
    }
  };

  /* -------------------------
                   UI: LOADING
        -------------------------- */
  if (loading)
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64 text-gray-600">
          Loading…
        </div>
      </AdminLayout>
    );

  if (!application) return null;

  const hasDocuments =
    application.documents && application.documents.length > 0;

  /* -------------------------
                   RENDER
        -------------------------- */
  return (
    <AdminLayout>
      <div className="flex gap-6 relative">
        {/* LEFT SIDE - 70% Main Content */}
        <div className={`${hasDocuments ? "w-[70%]" : "w-full"} space-y-10`}>
          {/* ---------------- HEADER ---------------- */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">Application Details</h2>
              <p className="text-gray-600 text-sm">
                Application ID: {application.id}
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate("/admin/applications")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button className="bg-green-600 text-white" onClick={exportZip}>
                <Download className="w-4 h-4 mr-2" />
                Download ZIP
              </Button>
            </div>
          </div>

          {/* ---------------- STATUS BLOCK ---------------- */}
          <div className="bg-white border rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600" />
              Application Status
            </h3>

            <div className="space-y-6">
              {/* Current Status */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Current Status
                </Label>
                <div className="mt-2">
                  <span
                    className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${badge(application.status)}`}
                  >
                    {application.status}
                  </span>
                </div>
              </div>

              {/* Status Actions */}
              {application.status ===
                "Application Received, Pending for Review" && (
                <div className="border-t pt-4">
                  <Button
                    className="bg-green-600 text-white hover:bg-green-700"
                    onClick={async () => {
                      await api.adminUpdateApplicationStatus(id, {
                        status: "Review Passed, Pending for Submission",
                        note: "Reviewed and ready for submission",
                      });
                      fetchApplication();
                    }}
                  >
                    Mark as Reviewed
                  </Button>
                </div>
              )}

              {application.status === "Additional Information Received" && (
                <div className="border-t pt-4">
                  <Button
                    className="bg-green-600 text-white hover:bg-green-700"
                    onClick={async () => {
                      await api.adminUpdateApplicationStatus(id, {
                        status: "Review Passed, Pending for Submission",
                        note: "Additional info reviewed",
                      });
                      fetchApplication();
                    }}
                  >
                    Mark as Reviewed
                  </Button>
                </div>
              )}

              {application.status ===
                "Review Passed, Pending for Submission" && (
                <div className="border-t pt-4">
                  <Button
                    className="bg-blue-600 text-white hover:bg-blue-700"
                    onClick={async () => {
                      await api.adminUpdateApplicationStatus(id, {
                        status: "Submission Done, Awaiting Visa Status",
                        note: "Application submitted to Embassy",
                      });
                      fetchApplication();
                    }}
                  >
                    Mark as Submitted
                  </Button>
                </div>
              )}

              {/* Final Decision */}
              {application.status ===
                "Submission Done, Awaiting Visa Status" && (
                <div className="border-t pt-4 space-y-3">
                  <Label className="text-base font-semibold">
                    Final Visa Status
                  </Label>

                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={statusForm.status}
                    onChange={(e) =>
                      setStatusForm({ ...statusForm, status: e.target.value })
                    }
                  >
                    <option value="">Select Status</option>
                    <option value="Visa Approved">Visa Approved</option>
                    <option value="Visa Rejected">Visa Rejected</option>
                  </select>

                  <Textarea
                    placeholder="Internal note (optional)"
                    value={statusForm.note}
                    onChange={(e) =>
                      setStatusForm({ ...statusForm, note: e.target.value })
                    }
                    className="w-full"
                  />

                  {statusForm.status === "Visa Rejected" && (
                    <Textarea
                      placeholder="Rejection Reason (required)"
                      value={statusForm.rejection_reason}
                      onChange={(e) =>
                        setStatusForm({
                          ...statusForm,
                          rejection_reason: e.target.value,
                        })
                      }
                      className="w-full border-red-300"
                    />
                  )}

                  <Button
                    className="bg-blue-600 text-white hover:bg-blue-700 w-full"
                    onClick={updateStatus}
                  >
                    Update Final Status
                  </Button>
                </div>
              )}

              {/* Visa Upload Section - Shows when Visa Approved */}
              {application.status === "Visa Approved" && (
                <div className="border-t pt-4 space-y-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Visa Document
                  </Label>

                  {!application.final_visa_uploaded ? (
                    <>
                      <p className="text-sm text-gray-600">
                        Upload the approved visa document for the applicant.
                      </p>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleVisaUpload(file);
                          }
                        }}
                        className="hidden"
                        id="visa-upload"
                      />
                      <Button
                        className="bg-green-600 text-white hover:bg-green-700 w-full"
                        onClick={() =>
                          document.getElementById("visa-upload")?.click()
                        }
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Visa Document
                      </Button>
                    </>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="text-green-700 font-medium">
                            Visa document uploaded successfully
                          </p>
                          <p className="text-sm text-green-600 mt-1">
                            The applicant can now download their visa
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ---------------- PASSENGERS ---------------- */}
          {passengers.map((pax, pIndex) => (
            <div
              key={pIndex}
              className="bg-white border rounded-lg p-6 shadow-sm"
            >
              {/* Passenger Header */}
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Passenger {pIndex + 1} — {pax.full_name}
                </h3>

                {editPassengerIndex === pIndex ? (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-green-600 text-white"
                      onClick={async () => {
                        try {
                          await api.adminUpdateApplication(application.id, {
                            passengers: editablePassengers,
                            status: application.status,
                          });

                          showToast("Passenger updated", "success");
                          setEditPassengerIndex(null);
                          fetchApplication();
                        } catch (e) {
                          showToast(
                            e.response?.data?.detail || "Update failed",
                            "error",
                          );
                        }
                      }}
                    >
                      <Save className="w-4 h-4 mr-1" />
                      Save Changes
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditablePassengers(
                          JSON.parse(JSON.stringify(passengers)),
                        );
                        setEditPassengerIndex(null);
                      }}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditablePassengers(
                        JSON.parse(JSON.stringify(passengers)),
                      );
                      setEditPassengerIndex(pIndex);
                    }}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit Passenger
                  </Button>
                )}
              </div>

              {/* Passenger Sections */}
              {/* {SECTIONS.map((section) => {
                const key = `p${pIndex}.${section.key}`;
                const isOpen = openSection[key];

                return (
                  <div key={section.key} className="border rounded mb-4">
                    <div
                      className="flex justify-between items-center px-4 py-2.5 bg-gray-50 cursor-pointer border-b"
                      onClick={() => toggleSection(key)}
                    >
                      <span className="text-sm font-medium flex items-center gap-2">
                        {isOpen ? (
                          <ChevronUp className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5" />
                        )}
                        {section.title}
                      </span>
                      <span className="text-xs text-gray-500">
                        {isOpen ? "Hide" : "Show"}
                      </span>
                    </div>

                    
                    {isOpen && (
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {section.fields.map(([field, label]) => {
                          const isSelectField = DROPDOWN_FIELDS[field];
                          const currentValue =
                            editPassengerIndex === pIndex
                              ? editablePassengers[pIndex]?.[field]
                              : pax[field];

                          return (
                            <div key={field}>
                              <Label className="text-xs text-gray-600">
                                {label}
                              </Label>

                              {editPassengerIndex === pIndex ? (
                              
                                isSelectField ? (
                         
                                  <select
                                    className="w-full border rounded px-3 py-1.5 text-sm mt-1 focus:ring-2 focus:ring-blue-500"
                                    value={currentValue || ""}
                                    onChange={(e) =>
                                      setEditablePassengers((prev) => {
                                        const copy = [...prev];
                                        copy[pIndex][field] = e.target.value;
                                        return copy;
                                      })
                                    }
                                  >
                                    <option value="">Select {label}</option>

                               
                                    {currentValue &&
                                      isSelectField.length > 0 &&
                                      !isSelectField.some((opt) => {
                                        const optValue =
                                          typeof opt === "string"
                                            ? opt
                                            : opt.value;
                                        return optValue === currentValue;
                                      }) && (
                                        <option
                                          value={currentValue}
                                          style={{ fontStyle: "italic" }}
                                        >
                                          {currentValue}
                                        </option>
                                      )}

                                    {isSelectField.map((opt) => {
                                      const optValue =
                                        typeof opt === "string"
                                          ? opt
                                          : opt.value;
                                      const optLabel =
                                        typeof opt === "string"
                                          ? opt
                                          : opt.label;

                                      return (
                                        <option key={optValue} value={optValue}>
                                          {optLabel}
                                        </option>
                                      );
                                    })}
                                  </select>
                                ) : (
                           
                                  <input
                                    className="w-full border rounded px-3 py-1.5 text-sm mt-1"
                                    value={currentValue ?? ""}
                                    onChange={(e) =>
                                      setEditablePassengers((prev) => {
                                        const copy = [...prev];
                                        copy[pIndex][field] = e.target.value;
                                        return copy;
                                      })
                                    }
                                  />
                                )
                              ) : (
                          
                                <div className="mt-1 text-sm text-gray-900">
                                  {pax[field] || "-"}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })} */}

{/* Passenger Sections */}
              {SECTIONS.map((section) => {
                const key = `p${pIndex}.${section.key}`;
                const isOpen = openSection[key];

                return (
                  <div key={section.key} className="border rounded mb-4">
                    <div
                      className="flex justify-between items-center px-4 py-2.5 bg-gray-50 cursor-pointer border-b"
                      onClick={() => toggleSection(key)}
                    >
                      <span className="text-sm font-medium flex items-center gap-2">
                        {isOpen ? (
                          <ChevronUp className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5" />
                        )}
                        {section.title}
                      </span>
                      <span className="text-xs text-gray-500">
                        {isOpen ? "Hide" : "Show"}
                      </span>
                    </div>

                    {isOpen && (
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {section.fields.map(([field, label]) => {
                          const isSelectField = DROPDOWN_FIELDS[field];
                          const currentValue =
                            editPassengerIndex === pIndex
                              ? editablePassengers[pIndex]?.[field]
                              : pax[field];

                          // ✅ Special handling for saarc_visits array
                          if (field === "saarc_visits") {
                            return (
                              <div key={field} className="md:col-span-2">
                                <Label className="text-xs text-gray-600 mb-2 block">
                                  {label}
                                </Label>

                                {currentValue &&
                                Array.isArray(currentValue) &&
                                currentValue.length > 0 ? (
                                  <div className="space-y-2">
                                    {currentValue.map((visit, visitIdx) => (
                                      <div
                                        key={visitIdx}
                                        className="border rounded p-3 bg-gray-50"
                                      >
                                        <div className="grid grid-cols-3 gap-3">
                                          <div>
                                            <p className="text-xs text-gray-500">
                                              Country
                                            </p>
                                            <p className="text-sm font-medium">
                                              {visit.saarc_country || "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-xs text-gray-500">
                                              Year
                                            </p>
                                            <p className="text-sm font-medium">
                                              {visit.saarc_year || "-"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-xs text-gray-500">
                                              No. of Visits
                                            </p>
                                            <p className="text-sm font-medium">
                                              {visit.no_of_visits || "-"}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-500 italic">
                                    No SAARC visits recorded
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // Regular field handling
                          return (
                            <div key={field}>
                              <Label className="text-xs text-gray-600">
                                {label}
                              </Label>

                              {editPassengerIndex === pIndex ? (
                                // EDIT MODE
                                isSelectField ? (
                                  // ✅ Render dropdown for select fields
                                  <select
                                    className="w-full border rounded px-3 py-1.5 text-sm mt-1 focus:ring-2 focus:ring-blue-500"
                                    value={currentValue || ""}
                                    onChange={(e) =>
                                      setEditablePassengers((prev) => {
                                        const copy = [...prev];
                                        copy[pIndex][field] = e.target.value;
                                        return copy;
                                      })
                                    }
                                  >
                                    <option value="">Select {label}</option>

                                    {/* ✅ Add extracted value if it doesn't match any option */}
                                    {currentValue &&
                                      isSelectField.length > 0 &&
                                      !isSelectField.some((opt) => {
                                        const optValue =
                                          typeof opt === "string"
                                            ? opt
                                            : opt.value;
                                        return optValue === currentValue;
                                      }) && (
                                        <option
                                          value={currentValue}
                                          style={{ fontStyle: "italic" }}
                                        >
                                          {currentValue}
                                        </option>
                                      )}

                                    {/* ✅ Regular dropdown options */}
                                    {isSelectField.map((opt) => {
                                      const optValue =
                                        typeof opt === "string"
                                          ? opt
                                          : opt.value;
                                      const optLabel =
                                        typeof opt === "string"
                                          ? opt
                                          : opt.label;

                                      return (
                                        <option key={optValue} value={optValue}>
                                          {optLabel}
                                        </option>
                                      );
                                    })}
                                  </select>
                                ) : (
                                  // ✅ Regular text input for non-select fields
                                  <input
                                    className="w-full border rounded px-3 py-1.5 text-sm mt-1"
                                    value={currentValue ?? ""}
                                    onChange={(e) =>
                                      setEditablePassengers((prev) => {
                                        const copy = [...prev];
                                        copy[pIndex][field] = e.target.value;
                                        return copy;
                                      })
                                    }
                                  />
                                )
                              ) : (
                                // VIEW MODE (read-only)
                                <div className="mt-1 text-sm text-gray-900">
                                  {pax[field] || "-"}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {/* ---------------- ADDITIONAL INFORMATION ---------------- */}
          <div className="bg-white border rounded-lg p-6 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Additional Information
              </h3>

              <Button
                className="bg-blue-600 text-white hover:bg-blue-700"
                onClick={() => setBuilderOpen(true)}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Request Additional Info
              </Button>
            </div>

            {loadingRounds ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-gray-500 text-sm">Loading...</p>
                </div>
              </div>
            ) : rounds.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 font-medium">
                  No additional information requested yet.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Click the button above to request information from the
                  applicant.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {rounds.map((r, i) => (
                  <div
                    key={r.id}
                    className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* Header */}
                    <div className="flex justify-between items-center bg-gray-50 px-4 py-3 border-b">
                      <div className="flex items-center gap-3">
                        <span className="bg-blue-600 text-white font-semibold px-3 py-1 rounded-full text-xs">
                          #{i + 1}
                        </span>
                        <strong className="text-gray-900">Round {i + 1}</strong>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          r.status === "completed"
                            ? "bg-green-100 text-green-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {r.status}
                      </span>
                    </div>

                    <div className="p-4 space-y-3">
                      {/* Admin note */}
                      {r.note_to_applicant && (
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                          <p className="text-xs font-semibold text-blue-900 mb-1">
                            ADMIN NOTE
                          </p>
                          <p className="text-sm text-blue-800">
                            {r.note_to_applicant}
                          </p>
                        </div>
                      )}

                      {/* User Responses */}
                      {r.user_response &&
                      Object.keys(r.user_response).length > 0 ? (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            Applicant Response
                          </p>

                          {r.fields.map((field) => {
                            const responseValue = r.user_response[field.key];
                            const isFile = field.type === "file";

                            return (
                              <div
                                key={field.key}
                                className="border rounded-lg px-3 py-2 bg-gray-50"
                              >
                                <p className="text-xs text-gray-600 mb-1 font-medium">
                                  {field.label}
                                </p>

                                {isFile && responseValue ? (
                                  <div className="flex items-center gap-2 bg-white border rounded px-3 py-2 mt-1">
                                    <FileText className="w-4 h-4 text-blue-600" />
                                    <p className="text-sm text-gray-700 flex-1 truncate">
                                      {responseValue.split("/").pop() ||
                                        "File uploaded"}
                                    </p>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-blue-600 border-blue-600 hover:bg-blue-50 flex-shrink-0"
                                      onClick={() =>
                                        window.open(responseValue, "_blank")
                                      }
                                    >
                                      <Eye className="w-3 h-3 mr-1" />
                                      View
                                    </Button>
                                  </div>
                                ) : (
                                  <p className="text-sm font-medium text-gray-900 mt-1">
                                    {responseValue || (
                                      <span className="text-gray-400">
                                        No response
                                      </span>
                                    )}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm text-yellow-800 font-medium">
                            ⏳ Awaiting applicant response
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDE - 30% Documents Panel (Fixed) */}
        {hasDocuments && (
          <div className="w-[30%]">
            <div className="sticky top-6">
              <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b px-4 py-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-700" />
                    Uploaded Documents
                  </h3>
                  <p className="text-xs text-gray-600 mt-1">
                    {application.documents.length} document
                    {application.documents.length !== 1 ? "s" : ""}
                  </p>
                </div>

                <div
                  className="overflow-y-auto"
                  style={{ maxHeight: "calc(100vh - 140px)" }}
                >
                  <div className="p-4 space-y-4">
                    {application.documents.map((doc, idx) => (
                      <div
                        key={doc.id}
                        className="border rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow"
                      >
                        {/* Document Header */}
                        <div className="px-3 py-2 bg-gray-50 border-b">
                          <div className="flex items-start gap-2">
                            <FileText className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-gray-900 truncate">
                                {doc.document_type_name || "Document"}
                              </p>
                              <p
                                className="text-xs text-gray-500 mt-0.5 truncate"
                                title={doc.file_name}
                              >
                                {doc.file_name}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Document Preview */}
                        <div className="p-3 bg-gray-50">
                          {doc.preview_url ? (
                            isPDF(doc.file_name) ? (
                              // PDF Preview using PdfPreview component
                              <div className="w-full bg-white border rounded overflow-hidden">
                                <div className="max-h-[400px] overflow-auto">
                                  <PdfPreview fileUrl={doc.preview_url} />
                                </div>
                              </div>
                            ) : (
                              // Image Preview
                              <div className="w-full bg-white border rounded overflow-hidden">
                                <img
                                  src={doc.preview_url}
                                  alt={doc.file_name}
                                  className="w-full h-auto object-contain"
                                  style={{ maxHeight: "400px" }}
                                />
                              </div>
                            )
                          ) : (
                            <div className="w-full h-40 bg-gray-100 border-2 border-dashed rounded flex items-center justify-center">
                              <div className="text-center">
                                <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                <p className="text-sm text-gray-500">
                                  Preview not available
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Document Actions */}
                        <div className="px-3 pb-3 pt-2 flex gap-2 bg-gray-50">
                          <button
                            className="flex-1 text-blue-600 hover:text-blue-700 text-xs font-medium px-3 py-2 bg-white border border-blue-200 rounded hover:bg-blue-50 transition-colors flex items-center justify-center gap-1"
                            onClick={() =>
                              setDocModal({ open: true, index: idx })
                            }
                          >
                            <Eye className="w-3 h-3" />
                            View Full
                          </button>

                          {doc.preview_url && (
                            <a
                              href={doc.preview_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 text-green-600 hover:text-green-700 text-xs font-medium px-3 py-2 bg-white border border-green-200 rounded hover:bg-green-50 transition-colors flex items-center justify-center gap-1"
                              download
                            >
                              <Download className="w-3 h-3" />
                              Download
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AdditionalInfoBuilderModal
        applicationId={application.id}
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        onCreated={() => {
          setBuilderOpen(false);
          fetchAdditionalInfoRounds();
        }}
      />

      <DocumentPreviewModal
        open={docModal.open}
        onClose={() => setDocModal({ open: false, index: 0 })}
        documents={application.documents}
        startIndex={docModal.index}
      />
    </AdminLayout>
  );
}
